import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  User, Building2, FileText, Camera, CheckCircle2, Upload, ArrowRight, ArrowLeft, Bike, Car, ShieldCheck,
} from "lucide-react";

const BANCOS = ["BBVA", "Santander", "Banorte", "Citibanamex", "HSBC", "Scotiabank", "Inbursa", "Banco Azteca", "BanBajío", "Otro"];

const PASOS = [
  { id: "personal", titulo: "Datos personales", Icon: User },
  { id: "banco", titulo: "Datos bancarios", Icon: Building2 },
  { id: "documentos", titulo: "Documentos", Icon: FileText },
  { id: "selfie", titulo: "Foto de perfil", Icon: Camera },
  { id: "deposito", titulo: "Depósito", Icon: ShieldCheck },
  { id: "review", titulo: "Confirmar", Icon: CheckCircle2 },
];

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function RepartidorOnboarding() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [paso, setPaso] = useState(0);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    nombre_completo: user?.nombre || "",
    telefono: user?.telefono || "",
    fecha_nacimiento: "",
    tipo_vehiculo: "Moto",
    banco: "",
    clabe: "",
    ciudad: "",
    estado: "",
    foto_selfie_url: "",
    doc_ine_url: "",
    doc_licencia_url: "",
    doc_tarjeta_circulacion_url: "",
    doc_comprobante_domicilio_url: "",
    deposito_aceptado: false,
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const uploadFile = async (campo, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Archivo máx 500KB"); return; }
    const dataUrl = await fileToDataURL(file);
    set(campo, dataUrl);
  };

  const canNext = () => {
    if (paso === 0) return f.nombre_completo && f.telefono && f.fecha_nacimiento && f.tipo_vehiculo;
    if (paso === 1) {
      const clabeOk = f.clabe.replace(/\s/g, "").length === 18;
      return f.banco && clabeOk;
    }
    if (paso === 2) return f.doc_ine_url && f.doc_licencia_url && f.doc_tarjeta_circulacion_url && f.doc_comprobante_domicilio_url;
    if (paso === 3) return f.foto_selfie_url;
    if (paso === 4) return f.deposito_aceptado;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/repartidores/onboarding", { ...f, clabe: f.clabe.replace(/\s/g, "") });
      toast.success("¡Solicitud enviada! Te avisaremos pronto.");
      await refresh();
      nav("/repartidor");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
        <div className="overline text-zinc-500">Onboarding</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">
          Completa tu perfil de <span className="text-[#FF3366]">Delivery Hero</span>
        </h1>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-1 overflow-x-auto pb-2">
          {PASOS.map((p, i) => {
            const activo = i === paso, hecho = i < paso;
            return (
              <div key={p.id} className="flex items-center gap-1 shrink-0" data-testid={`step-${p.id}`}>
                <div className={`w-9 h-9 rounded-sm grid place-items-center border-2 transition-colors ${
                  hecho ? "bg-emerald-500 border-emerald-500 text-white"
                    : activo ? "bg-[#FF3366] border-[#FF3366] text-white"
                      : "bg-white border-zinc-200 text-zinc-400"
                }`}>
                  {hecho ? <CheckCircle2 className="w-4 h-4" /> : <p.Icon className="w-4 h-4" />}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${activo ? "text-zinc-900" : "text-zinc-500"}`}>{p.titulo}</span>
                {i < PASOS.length - 1 && <div className="w-6 h-px bg-zinc-300 mx-1" />}
              </div>
            );
          })}
        </div>

        <motion.div
          key={paso}
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="mt-8 bg-white border border-zinc-200 rounded-sm p-6 lg:p-8"
        >
          {paso === 0 && (
            <div className="space-y-4">
              <div>
                <Label>Nombre completo</Label>
                <Input data-testid="onb-nombre" value={f.nombre_completo} onChange={e => set("nombre_completo", e.target.value)} className="mt-1 rounded-sm h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Teléfono</Label>
                  <Input data-testid="onb-telefono" value={f.telefono} onChange={e => set("telefono", e.target.value)} placeholder="+52 55…" className="mt-1 rounded-sm h-11" />
                </div>
                <div>
                  <Label>Fecha de nacimiento</Label>
                  <Input data-testid="onb-nacimiento" type="date" value={f.fecha_nacimiento} onChange={e => set("fecha_nacimiento", e.target.value)} className="mt-1 rounded-sm h-11" />
                </div>
              </div>
              <div>
                <Label>Tipo de vehículo</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[
                    { v: "Moto", label: "Moto", Icon: Bike },
                    { v: "Auto", label: "Auto", Icon: Car },
                    { v: "Bicicleta", label: "Bicicleta", Icon: Bike },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`veh-${opt.v}`}
                      onClick={() => set("tipo_vehiculo", opt.v)}
                      className={`p-4 rounded-sm border-2 flex flex-col items-center gap-2 transition-colors ${
                        f.tipo_vehiculo === opt.v ? "border-[#FF3366] bg-[#FF3366]/5" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <opt.Icon className="w-6 h-6" />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ciudad</Label>
                  <Input data-testid="onb-ciudad" value={f.ciudad} onChange={e => set("ciudad", e.target.value)} className="mt-1 rounded-sm h-11" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Input data-testid="onb-estado" value={f.estado} onChange={e => set("estado", e.target.value)} className="mt-1 rounded-sm h-11" />
                </div>
              </div>
            </div>
          )}

          {paso === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Banco</Label>
                <Select value={f.banco} onValueChange={v => set("banco", v)}>
                  <SelectTrigger data-testid="onb-banco" className="mt-1 rounded-sm h-11"><SelectValue placeholder="Selecciona tu banco" /></SelectTrigger>
                  <SelectContent>{BANCOS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>CLABE (18 dígitos)</Label>
                <Input
                  data-testid="onb-clabe"
                  value={f.clabe}
                  onChange={e => set("clabe", e.target.value.replace(/\D/g, "").slice(0, 18))}
                  placeholder="012345678901234567"
                  className="mt-1 rounded-sm h-11 font-mono"
                  maxLength={18}
                />
                <div className="mt-1 text-xs text-zinc-500">
                  {f.clabe.length}/18 dígitos · Tus ganancias se depositarán aquí semanalmente
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-800">
                🔒 Tu CLABE se encripta con Fernet en nuestros servidores. Solo tú y el equipo de finanzas la vemos.
              </div>
            </div>
          )}

          {paso === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">Sube fotos claras (frente completo, sin recortar). Formato JPG/PNG, máx 500KB cada una.</p>
              {[
                { k: "doc_ine_url", label: "INE / Pasaporte", desc: "Documento oficial vigente" },
                { k: "doc_licencia_url", label: "Licencia de conducir", desc: "Debe estar vigente" },
                { k: "doc_tarjeta_circulacion_url", label: "Tarjeta de circulación", desc: "Del vehículo que usarás" },
                { k: "doc_comprobante_domicilio_url", label: "Comprobante de domicilio", desc: "Recibo de luz, agua o teléfono (< 3 meses)" },
              ].map(doc => (
                <DocUploader key={doc.k} campo={doc.k} label={doc.label} desc={doc.desc} valor={f[doc.k]} onChange={e => uploadFile(doc.k, e)} onClear={() => set(doc.k, "")} />
              ))}
            </div>
          )}

          {paso === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">Tómate una selfie con buena luz. Sin lentes oscuros ni gorra.</p>
              <div className="flex flex-col items-center gap-4">
                {f.foto_selfie_url ? (
                  <div className="relative">
                    <img src={f.foto_selfie_url} alt="selfie" className="w-48 h-48 object-cover rounded-full border-4 border-[#FF3366]" />
                    <button
                      data-testid="clear-selfie"
                      onClick={() => set("foto_selfie_url", "")}
                      className="absolute -top-2 -right-2 bg-white border border-zinc-300 rounded-full w-8 h-8 text-xs"
                    >✕</button>
                  </div>
                ) : (
                  <div className="w-48 h-48 rounded-full border-4 border-dashed border-zinc-300 grid place-items-center text-zinc-400">
                    <Camera className="w-12 h-12" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input data-testid="onb-selfie" type="file" accept="image/*" capture="user" onChange={e => uploadFile("foto_selfie_url", e)} className="hidden" />
                  <span className="inline-flex items-center gap-2 px-4 h-11 rounded-sm bg-zinc-900 hover:bg-[#FF3366] text-white text-sm font-medium">
                    <Upload className="w-4 h-4" /> {f.foto_selfie_url ? "Cambiar selfie" : "Subir selfie"}
                  </span>
                </label>
              </div>
            </div>
          )}

          {paso === 4 && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-sm bg-[#D4FF00] grid place-items-center">
                  <ShieldCheck className="w-8 h-8 text-zinc-900" strokeWidth={2.5} />
                </div>
                <h3 className="mt-4 font-display font-black text-2xl tracking-tighter">Depósito en garantía</h3>
                <div className="mt-2 font-display font-black text-5xl tracking-tighter text-[#FF3366]">$2,000 MXN</div>
                <div className="text-sm text-emerald-600 font-medium mt-1">100% reembolsable</div>
              </div>

              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span><b>No es una cuota ni comisión</b> — es tu propio dinero guardado como respaldo.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Se libera automáticamente al completar <b>50 entregas exitosas</b> o al dar de baja tu cuenta.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Cubre faltantes o daños a mercancía. Sin faltantes = reembolso íntegro.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Se deposita a tu <b>CLABE registrada</b> (paso anterior) en 5 días hábiles al liberarse.</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                💰 El cobro del depósito se procesa <b>después</b> de que verifiquemos tus documentos. Recibirás un link seguro por correo para pagar con tarjeta o transferencia. Tu cuenta se activa al recibir el depósito.
              </div>

              <label className="flex items-start gap-3 bg-white border-2 border-zinc-200 hover:border-[#FF3366] rounded-sm p-4 cursor-pointer transition-colors">
                <input
                  data-testid="onb-deposito-aceptado"
                  type="checkbox"
                  checked={f.deposito_aceptado}
                  onChange={e => set("deposito_aceptado", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#FF3366]"
                />
                <span className="text-sm">
                  Acepto el <b>depósito en garantía de $2,000 MXN 100% reembolsable</b> y entiendo que se cobrará después de la aprobación de mis documentos antes de recibir mi primera entrega.
                </span>
              </label>
            </div>
          )}

          {paso === 5 && (
            <div className="space-y-4">
              <h3 className="font-display font-bold text-xl tracking-tight">Revisa tu información</h3>
              <div className="grid grid-cols-2 gap-4">
                {f.foto_selfie_url && (
                  <img src={f.foto_selfie_url} alt="selfie" className="w-32 h-32 object-cover rounded-full border-2 border-zinc-200" />
                )}
                <div className="space-y-1 text-sm">
                  <div><b>Nombre:</b> {f.nombre_completo}</div>
                  <div><b>Teléfono:</b> {f.telefono}</div>
                  <div><b>Nacimiento:</b> {f.fecha_nacimiento}</div>
                  <div><b>Vehículo:</b> {f.tipo_vehiculo}</div>
                  <div><b>Banco:</b> {f.banco}</div>
                  <div><b>CLABE:</b> ••••••{f.clabe.slice(-4)}</div>
                  <div><b>Ubicación:</b> {f.ciudad}, {f.estado}</div>
                  <div className="text-emerald-600 font-medium mt-2">✓ Depósito $2,000 MXN aceptado</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["INE", f.doc_ine_url],
                  ["Licencia", f.doc_licencia_url],
                  ["Circulación", f.doc_tarjeta_circulacion_url],
                  ["Domicilio", f.doc_comprobante_domicilio_url],
                ].map(([l, u]) => (
                  <div key={l} className="border border-zinc-200 rounded-sm p-2">
                    <div className="text-xs text-zinc-500 mb-1">{l}</div>
                    {u ? <img src={u} alt={l} className="w-full h-24 object-cover rounded-sm" /> : <div className="h-24 grid place-items-center text-zinc-400 text-xs">Faltante</div>}
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                ⚠️ Al enviar, tu cuenta pasará a "En Revisión". No podrás editar hasta que sea aprobada. Revisamos en 24-48h y luego te enviaremos el link de pago del depósito.
              </div>
            </div>
          )}
        </motion.div>

        {/* Navegación */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            data-testid="onb-back"
            onClick={() => setPaso(p => Math.max(0, p - 1))}
            disabled={paso === 0}
            className="rounded-sm h-11 px-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
          </Button>
          {paso < PASOS.length - 1 ? (
            <Button
              data-testid="onb-next"
              onClick={() => setPaso(p => p + 1)}
              disabled={!canNext()}
              className="rounded-sm h-11 px-8 bg-[#FF3366] hover:bg-[#E62958]"
            >
              Continuar <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="onb-submit"
              onClick={submit}
              disabled={loading}
              className="rounded-sm h-11 px-8 bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {loading ? "Enviando…" : "Enviar solicitud"} <CheckCircle2 className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function DocUploader({ campo, label, desc, valor, onChange, onClear }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-medium text-sm">{label}</div>
          <div className="text-xs text-zinc-500">{desc}</div>
        </div>
        {valor && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 h-16 rounded-sm bg-zinc-50 border border-zinc-200 overflow-hidden grid place-items-center">
          {valor ? <img src={valor} alt={label} className="w-full h-full object-cover" /> : <FileText className="w-6 h-6 text-zinc-300" />}
        </div>
        <label className="cursor-pointer">
          <input data-testid={`onb-${campo}`} type="file" accept="image/*" onChange={onChange} className="hidden" />
          <span className="inline-flex items-center gap-1.5 px-3 h-9 rounded-sm border border-zinc-300 hover:border-zinc-900 text-sm font-medium">
            <Upload className="w-3.5 h-3.5" /> {valor ? "Reemplazar" : "Subir"}
          </span>
        </label>
        {valor && (
          <button onClick={onClear} className="text-xs text-zinc-500 hover:text-red-600">Quitar</button>
        )}
      </div>
    </div>
  );
}
